'use client'

import { Branch } from '@/types/branch'
import { FeedListItem } from '@/types/feed'
import { Button, Flex, Icon, Spacer, Stack, Link, Text, Heading, Box, Center, Spinner } from '@/components/Chakra'
import { HiMap } from 'react-icons/hi'
import { getPrefText } from '@/lib/pref'
import { BiLinkExternal } from 'react-icons/bi'
import { CompanyContent } from './CompanyContent'
import { StackTable } from '@/components/StackTable'
import { SwiperSlide } from 'swiper/react'
import { JobSimpleCard } from '@/features/jobs/components/JobSimpleCard'
import { useEffect, useState } from 'react'
import { fetchJobsAction } from '@/features/jobs/action'
import { ESJob } from '@/types/job'
import { SummarizedCard as FeedCard } from '@/features/feeds/client'
import dynamic from 'next/dynamic'

type Props = {
  branch: Branch
  feeds: FeedListItem[]
  handleTabChange?: (index: number) => void
}

export const BranchAbout = ({ branch, feeds, handleTabChange }: Props) => {
  const SwiperWrap = dynamic(() => import('./SwiperWrap').then((c) => c.SwiperWrap), {
    loading: () => (
      <>
        <Center mt={[1, 1, 1]}>
          <Spinner thickness="4px" speed="0.65s" emptyColor="gray.100" color="primary.500" size="xl" />
        </Center>
      </>
    ),
    ssr: false,
  })

  const [jobs, setJobs] = useState<ESJob[]>([])

  const toGoogleMapLink = () => {
    if (branch.lat && branch.lng) {
      return `https://www.google.com/maps/search/?api=1&query=${branch.lat},${branch.lng}`
    } else {
      return `https://www.google.com/maps/search/?api=1&query=${getPrefText(branch.pref_id)}${branch.city_name}${
        branch.address
      }`
    }
  }

  useEffect(() => {
    fetchJobsAction({ 'branch_ids[]': branch.id, limit: 6, sort: 'newest' })
      .then((res) => {
        if (!res.ok) return
        setJobs(res.payload)
      })
      .catch((_e) => {})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <>
      {branch.company.company_contents.map((companyContent) => {
        return <CompanyContent key={companyContent.id} companyContent={companyContent} />
      })}

      <Box position={'relative'}>
        <Flex mb={3} pb={6} borderBottom={'1px solid'} borderColor={'blackAlpha.100'}>
          <Heading fontSize={'2xl'} as={'p'}>
            投稿フィード
          </Heading>
          <Spacer />
          <Link
            fontSize={'sm'}
            color={'gray.500'}
            fontWeight={'bold'}
            onClick={() => {
              handleTabChange && handleTabChange(1)
            }}
          >
            すべての投稿を見る
          </Link>
        </Flex>
        {feeds.length === 0 ? (
          <Center pt={10}>
            <Text fontWeight={'bold'} color={'gray.400'}>
              フィードの投稿はありません
            </Text>
          </Center>
        ) : (
          <SwiperWrap>
            {feeds.map((feed) => {
              return (
                <SwiperSlide style={{ height: 'auto', borderRight: '1px solid rgba(0, 0, 0, 0.06)' }} key={feed.id}>
                  <FeedCard feed={feed} width={'auto'} />
                </SwiperSlide>
              )
            })}
          </SwiperWrap>
        )}
      </Box>

      <Box position={'relative'}>
        <Flex mt={16} mb={3} pb={6} borderBottom={'1px solid'} borderColor={'blackAlpha.100'}>
          <Heading fontSize={'2xl'} as={'p'}>
            募集一覧
          </Heading>
          <Spacer />
          <Link
            fontSize={'sm'}
            color={'gray.500'}
            fontWeight={'bold'}
            onClick={() => {
              handleTabChange && handleTabChange(2)
            }}
          >
            すべての募集を見る
          </Link>
        </Flex>
        {jobs.length === 0 ? (
          <Center pt={10}>
            <Text fontWeight={'bold'} color={'gray.400'}>
              募集はありません
            </Text>
          </Center>
        ) : (
          <SwiperWrap>
            {jobs.map((job) => {
              return (
                <SwiperSlide key={job.id}>
                  <JobSimpleCard job={job} />
                </SwiperSlide>
              )
            })}
          </SwiperWrap>
        )}
      </Box>

      <Flex mt={16} pb={6} borderBottom={'1px solid'} borderColor={'blackAlpha.100'}>
        <Heading fontSize={'2xl'} as={'p'}>
          企業情報
        </Heading>
      </Flex>

      <StackTable title={'代表者名'}>
        <Text>
          {branch.company.representative_last_name}
          {branch.company.representative_first_name}
        </Text>
      </StackTable>

      <StackTable title={'所在地'}>
        <Flex width={['100%', '100%', '70%']}>
          <Stack>
            <Text>〒{branch.postal}</Text>
            <Text>
              {getPrefText(branch.pref_id)}
              {branch.city_name}
              {branch.address}
            </Text>
          </Stack>
          <Spacer />
          <Button
            target={'_blank'}
            as={'a'}
            href={toGoogleMapLink()}
            variant={'ghost'}
            border={'1px solid'}
            borderColor={'gray.200'}
            color={'primary.500'}
            size={'sm'}
            leftIcon={<HiMap />}
          >
            <Text color={'black'}>MAP</Text>
          </Button>
        </Flex>
      </StackTable>

      <StackTable title={'設立'}>
        <Text>{branch.company.foundation_year_month}</Text>
      </StackTable>

      <StackTable title={'業種'}>
        <Text>{branch.company.industry.name}</Text>
      </StackTable>

      <StackTable title={'資本金'}>
        <Text>{branch.company.capital}円</Text>
      </StackTable>

      <StackTable title={'従業員数'}>
        <Text>{branch.company.employees}人</Text>
      </StackTable>

      <StackTable title={'HPリンク'}>
        <Stack direction={'row'} align={'center'}>
          <Link className={'text-link'} color={'primary.500'} href={branch.company.site_url} target={'_blank'}>
            {branch.company.site_url}
          </Link>
          <Icon color={'primary.500'} as={BiLinkExternal} />
        </Stack>
      </StackTable>
    </>
  )
}
